import { useCallback, useContext, useState } from 'react';
import { useSnackbar } from '../../../app/hooks/useSnackbar';
import { MimeTypeToExtension } from '../helpers';
import { BusinessFileHttpService } from '../services';
import { DocumentsContext } from '../context';
import {
  BusinessDocumentTypeLabel,
  DOCUMENT_TYPE,
} from 'app/helpers/documentTypes';

export interface IFile {
  id?: string;
  file_name?: string;
  uploaded?: boolean;
  file: File | null;
  error?: boolean;
  type: DOCUMENT_TYPE;
  created_at?: string;
  invalidSize?: boolean;
  file_extension?: string;
}

const useUploadBusinessFiles = () => {
  const [uploadedFiles, setUploadedFiles] = useState<IFile[]>([]);
  const [uploadedFilesLoading, setUploadedFilesLoading] = useState(false);
  const [deleteRequestLoading, setDeleteRequestLoading] = useState(false);
  const [downloadRequestLoading, setDownloadRequestLoading] = useState(false);
  const { onOpenModal, onCloseModal } = useContext(DocumentsContext);
  const { showSnackbar } = useSnackbar();

  const updateFile = useCallback((id: string, data: Partial<IFile>) => {
    setUploadedFiles((state) =>
      state.map((file) => (file.id === id ? { ...file, ...data } : file)),
    );
  }, []);

  const removeFile = (id: string) => {
    setUploadedFiles((state) => state.filter((item) => item.id !== id));
  };

  const addFile = useCallback((data: IFile) => {
    setUploadedFiles((state) => state.concat(data));
  }, []);

  const processUpload = useCallback(
    async (uploadedFile: IFile, businessId: string) => {
      setUploadedFilesLoading(true);
      const data = new FormData();
      if (uploadedFile.file) {
        data.append('file', uploadedFile.file);
        data.append('type', uploadedFile.type);
      }

      const businessFilesHttpService = new BusinessFileHttpService();
      businessFilesHttpService.setHeader({
        'Content-Type': `multipart/form-data`,
      });

      try {
        const result = await businessFilesHttpService.uploadFilesToBusiness(
          data,
          businessId,
        );

        const updatedFile = {
          ...uploadedFile,
          id: result.data.id,
          uploaded: true,
          created_at: result.data.created_at,
        };

        addFile(updatedFile);
        setUploadedFilesLoading(false);
      } catch (error) {
        showSnackbar('Ocorreu um erro ao fazer o upload do arquivo');
        setUploadedFilesLoading(false);
      }
    },
    [updateFile],
  );

  const handleFileExtension = (file: File) => {
    const nameSplitted = file.name?.split('.');
    return nameSplitted[nameSplitted?.length - 1 || 0];
  };

  const handleUploadFiles = useCallback(
    (files: File[], businessId: string) => {
      const file = files[0];
      if (Number((file.size / (1024 * 1024)).toFixed(2)) > 20) {
        showSnackbar(
          `Arquivo inválido. ${file.name} possui tamanho superior a 20MB`,
        );
        return;
      }
      const excludeTypes = uploadedFiles.map(
        (uploadedFile) => uploadedFile.type,
      );
      onOpenModal(file.name, BusinessDocumentTypeLabel, excludeTypes).then(
        (type) => {
          if (type !== false) {
            processUpload(
              {
                file,
                type,
                file_name: file.name,
                uploaded: false,
                error: false,
                file_extension: handleFileExtension(file) || 'file',
              },
              businessId,
            ).then(() => onCloseModal());
          }
        },
      );
    },
    [uploadedFiles],
  );

  const fetchBusinessFile = async (
    businessId: string,
    originatorId?: string,
  ): Promise<IFile[]> => {
    try {
      const businessFilesHttpService = new BusinessFileHttpService();
      if (originatorId) {
        businessFilesHttpService.setOriginatorId(originatorId);
      }
      const result = await businessFilesHttpService.getFilesByBusiness(
        businessId,
      );
      setUploadedFiles(result.data);
      return result.data;
    } catch (error) {
      console.error('Erro ao buscar o arquivo', error);
      return [];
    }
  };

  const handleDeleteBusinessFile = useCallback(
    async (fileId: string, businessId: string) => {
      setDeleteRequestLoading(true);
      try {
        const businessFilesHttpService = new BusinessFileHttpService();
        await businessFilesHttpService.deleteFilesByBusiness(
          businessId,
          fileId,
        );
        removeFile(fileId);
        setDeleteRequestLoading(false);
      } catch (error) {
        console.error('Erro ao deletar o arquivo', error);
        setDeleteRequestLoading(false);
        showSnackbar('Ocorreu um erro ao tentar remover o arquivo');
      }
    },
    [],
  );

  const downloadBusinessFile = useCallback(
    async (businessId: string, fileId: string, file: IFile) => {
      setDownloadRequestLoading(true);
      try {
        const businessFilesHttpService = new BusinessFileHttpService();
        businessFilesHttpService.setRequestConfig({
          responseType: 'arraybuffer',
        });
        const result = await businessFilesHttpService.downloadBusinessFile(
          businessId,
          fileId,
        );
        const fileData = result.data;
        const fileContentType = result.headers?.['content-type'];
        const url = URL.createObjectURL(new Blob([fileData]));
        const link = document.createElement('a');
        const hasExtension = Object.values(MimeTypeToExtension).findIndex(
          (ext) => file?.file_name?.includes(`.${ext}`),
        );
        link.href = url;
        if (hasExtension === -1) {
          link.setAttribute(
            'download',
            `${file?.file_name || 'document'}${
              MimeTypeToExtension[fileContentType || 'txt']
                ? `.${MimeTypeToExtension[fileContentType || 'txt']}`
                : ''
            }`,
          );
        } else {
          link.setAttribute('download', file.file_name!);
        }
        link.click();
        link.remove();
        URL.revokeObjectURL(url);
      } catch (error) {
        console.error('Erro ao tentar fazer o download do arquivo', error);
        showSnackbar('Ocorreu um erro ao tentar fazer o download do arquivo');
      }
    },
    [],
  );

  return {
    handleUploadFiles,
    processUpload,
    fetchBusinessFile,
    handleDeleteBusinessFile,
    downloadBusinessFile,
    uploadedFiles,
    uploadedFilesLoading,
    deleteRequestLoading,
    downloadRequestLoading,
  };
};

export default useUploadBusinessFiles;
